import React, { useEffect, useRef, useState } from "react";
import {
  Container, Card, CardBody, Table, Button, Spinner, Badge, Row, Col,
  Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup, Alert,
} from "reactstrap";
import { Link, useSearchParams } from "react-router-dom";
import {
  getPropertiesByOwner, createProperty, getTenantsByProperty,
  getDeletedPropertiesByOwner, restoreProperty,
  getPropertiesSharedWithEmail, canEditProperty,
} from "../../../helpers/firestoreService";
import { useAuthUser } from "../../../hooks/AuthProvider";
import homeBg from "../../../assets/images/home.png";

const PropertyList = () => {
  document.title = "Properties | Support Portal";

  const user = useAuthUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [tenantCounts, setTenantCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(!!searchParams.get("openCreate"));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("address");
  const [sortDir, setSortDir] = useState("asc");
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [view, setView] = useState("mine"); // "mine" | "shared"
  const [sharedCount, setSharedCount] = useState(0);

  // New property form - structured address fields
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [country, setCountry] = useState("USA");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [addressVerified, setAddressVerified] = useState(false);
  const [placeSearch, setPlaceSearch] = useState("");
  const placeInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Load Google Places script once
  useEffect(() => {
    const key = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY;
    if (!key) return;
    if (document.querySelector('[data-gmaps]')) return;
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.dataset.gmaps = "1";
    document.head.appendChild(script);
  }, []);

  // Attach Autocomplete directly to the search input when modal opens
  useEffect(() => {
    if (!createModal) { autocompleteRef.current = null; return; }

    const attach = () => {
      const inputEl = placeInputRef.current;
      if (!inputEl || !window.google?.maps?.places?.Autocomplete) return false;
      if (autocompleteRef.current) return true;

      const ac = new window.google.maps.places.Autocomplete(inputEl, {
        types: ["address"],
        fields: ["address_components", "formatted_address"],
      });

      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (!place.address_components) return;
        let num = "", route = "", cityV = "", stateV = "", zipV = "", countryV = "";
        for (const c of place.address_components) {
          if (c.types.includes("street_number")) num = c.long_name;
          if (c.types.includes("route")) route = c.long_name;
          if (c.types.includes("locality")) cityV = c.long_name;
          if (c.types.includes("administrative_area_level_1")) stateV = c.short_name;
          if (c.types.includes("postal_code")) zipV = c.long_name;
          if (c.types.includes("country")) countryV = c.short_name;
        }
        setStreet(`${num} ${route}`.trim());
        setCity(cityV);
        setState(stateV);
        setZip(zipV);
        setCountry(countryV || "USA");
        setPlaceSearch(place.formatted_address || "");
        setAddressVerified(true);
      });

      autocompleteRef.current = ac;
      return true;
    };

    if (!attach()) {
      const timer = setInterval(() => { if (attach()) clearInterval(timer); }, 200);
      return () => clearInterval(timer);
    }
  }, [createModal]);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      let props;
      if (view === "shared") {
        props = (await getPropertiesSharedWithEmail(user.email)).filter(
          (p) => p.createdBy !== user.uid
        );
      } else if (showDeleted) {
        props = await getDeletedPropertiesByOwner(user.uid);
      } else {
        props = await getPropertiesByOwner(user.uid);
      }
      setProperties(props);

      // Load tenant counts for each property
      const counts = {};
      await Promise.all(
        props.map((p) =>
          getTenantsByProperty(p.id)
            .then((tenants) => { counts[p.id] = tenants.length; })
            .catch(() => { counts[p.id] = 0; })
        )
      );
      setTenantCounts(counts);
    } catch (e) {
      console.error("Failed to load properties:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [user, showDeleted, view]);

  // Keep the "Shared with me" tab badge up to date
  useEffect(() => {
    if (!user?.email) return;
    getPropertiesSharedWithEmail(user.email)
      .then((shared) => setSharedCount(shared.filter((p) => p.createdBy !== user.uid).length))
      .catch(() => setSharedCount(0));
  }, [user]);

  const handleCreate = async () => {
    setError("");
    
    // Validate required address fields
    if (!street.trim() || !city.trim() || !state.trim() || !zip.trim()) {
      setError("Street, city, state, and ZIP are required.");
      return;
    }
    
    // Validate consent
    if (!agreeToTerms) {
      setError("You must agree to the Terms of Service and Privacy Policy.");
      return;
    }
    
    setSaving(true);
    try {
      // Compose formatted address
      const formattedAddress = `${street.trim()}, ${city.trim()}, ${state.trim()} ${zip.trim()}${country && country !== "USA" ? `, ${country}` : ""}`;
      
      await createProperty({
        address: formattedAddress,
        createdBy: user.uid,
      });
      
      // Reset form fields
      setStreet("");
      setCity("");
      setState("");
      setZip("");
      setCountry("USA");
      setAgreeToTerms(false);
      setAddressVerified(false);
      setPlaceSearch("");
      setCreateModal(false);
      
      // Clear the openCreate query param if present
      if (searchParams.has("openCreate")) {
        setSearchParams({});
      }
      
      await load();
    } catch (e) {
      console.error("Create property failed:", e);
      setError("Failed to create property. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (propertyId) => {
    setRestoring(propertyId);
    try {
      await restoreProperty(propertyId);
      setProperties((prev) => prev.filter((p) => p.id !== propertyId));
    } catch (e) {
      console.error("Restore property failed:", e);
    } finally {
      setRestoring(null);
    }
  };

  if (loading) {
    return (
      <div className="page-content text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Hero banner with home.png background */}
        <div
          className="rounded mb-4 p-4 text-white position-relative overflow-hidden"
          style={{ minHeight: 180 }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, #556ee6 0%, #34c38f 100%)",
            }}
          />
          <div className="position-relative" style={{ zIndex: 1 }}>
            <Row className="align-items-center">
              <Col>
                <h3 className="mb-1 text-white">Properties</h3>
                <p className="mb-0 opacity-75">
                  {view === "shared"
                    ? "Properties other people have shared with you."
                    : "Manage your properties and assigned tenants in one place."}
                </p>
              </Col>
              {view === "mine" && (
                <>
                  <Col xs="auto">
                    <Button
                      color="light"
                      onClick={() => {
                        setError("");
                        setStreet("");
                        setCity("");
                        setState("");
                        setZip("");
                        setCountry("USA");
                        setAgreeToTerms(false);
                        setAddressVerified(false);
                        setPlaceSearch("");
                        setCreateModal(true);
                      }}
                    >
                      <i className="bx bx-plus me-1" /> Add Property
                    </Button>
                  </Col>
                  <Col xs="auto">
                    <Button
                      color={showDeleted ? "warning" : "light"}
                      onClick={() => { setShowDeleted((v) => !v); setLoading(true); }}
                    >
                      <i className={`bx ${showDeleted ? "bx-hide" : "bx-trash"} me-1`} />
                      {showDeleted ? "Hide Deleted" : "Show Deleted"}
                    </Button>
                  </Col>
                </>
              )}
            </Row>
            <Row className="mt-3 align-items-center">
              <Col xs="auto">
                <div className="btn-group btn-group-sm">
                  <Button
                    color={view === "mine" ? "light" : "outline-light"}
                    onClick={() => { if (view !== "mine") { setView("mine"); setLoading(true); } }}
                  >
                    <i className="bx bx-home me-1" /> My Properties
                  </Button>
                  <Button
                    color={view === "shared" ? "light" : "outline-light"}
                    onClick={() => { if (view !== "shared") { setView("shared"); setShowDeleted(false); setLoading(true); } }}
                  >
                    <i className="bx bx-share-alt me-1" /> Shared with me
                    {sharedCount > 0 && (
                      <Badge color="danger" pill className="ms-1">{sharedCount}</Badge>
                    )}
                  </Button>
                </div>
              </Col>
              <Col xs="auto">
                <div className="d-flex align-items-center gap-2">
                  <Badge color="light" className="text-dark fs-6 px-3 py-2">
                    <i className="bx bx-building-house me-1" />
                    {properties.length} {properties.length === 1 ? "Property" : "Properties"}
                  </Badge>
                  <Badge color="light" className="text-dark fs-6 px-3 py-2">
                    <i className="bx bx-user me-1" />
                    {Object.values(tenantCounts).reduce((a, b) => a + b, 0)} Total Tenants
                  </Badge>
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {properties.length === 0 ? (
          <Card>
            <CardBody className="text-center py-5">
              <i className={`bx ${view === "shared" ? "bx-share-alt" : "bx-building-house"} text-muted`} style={{ fontSize: "2.5rem" }} />
              {view === "shared" ? (
                <>
                  <h5 className="text-muted mt-2">Nothing shared with you yet</h5>
                  <p className="text-muted mb-0">
                    When someone shares a property with <strong>{user?.email}</strong>, it will show up here.
                  </p>
                </>
              ) : (
                <>
                  <h5 className="text-muted mt-2">No properties yet</h5>
                  <p className="text-muted">
                    Add your first property to start managing tenants and assignments.
                  </p>
                  <Button color="primary" onClick={() => { setError(""); setCreateModal(true); }}>
                    Add Property
                  </Button>
                </>
              )}
            </CardBody>
          </Card>
        ) : (() => {
          const q = search.toLowerCase();
          const filtered = properties
            .filter((p) => !q || (p.address || "").toLowerCase().includes(q))
            .sort((a, b) => {
              let av, bv;
              if (sortField === "address") { av = (a.address || "").toLowerCase(); bv = (b.address || "").toLowerCase(); }
              else if (sortField === "tenants") { av = tenantCounts[a.id] || 0; bv = tenantCounts[b.id] || 0; }
              else { av = a.createdAt || new Date(0); bv = b.createdAt || new Date(0); }
              if (av < bv) return sortDir === "asc" ? -1 : 1;
              if (av > bv) return sortDir === "asc" ? 1 : -1;
              return 0;
            });
          const toggleSort = (field) => {
            if (sortField === field) setSortDir((d) => d === "asc" ? "desc" : "asc");
            else { setSortField(field); setSortDir("asc"); }
          };
          const SortIcon = ({ field }) => sortField !== field ? null : (
            <i className={`bx bx-chevron-${sortDir === "asc" ? "up" : "down"} ms-1`} />
          );
          return (
            <Card className="border-0 shadow-sm">
              <CardBody>
                <Row className="mb-3 align-items-center">
                  <Col md={4}>
                    <Input
                      type="text"
                      placeholder="Search by address…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      bsSize="sm"
                    />
                  </Col>
                  <Col xs="auto" className="ms-auto text-muted small">
                    Showing {filtered.length} of {properties.length}
                  </Col>
                </Row>
                {filtered.length === 0 ? (
                  <p className="text-muted text-center py-3">No properties match your search.</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0">
                      <thead>
                        <tr>
                          <th style={{ cursor: "pointer" }} onClick={() => toggleSort("address")}>
                            Address <SortIcon field="address" />
                          </th>
                          <th style={{ cursor: "pointer" }} onClick={() => toggleSort("tenants")}>
                            Tenants <SortIcon field="tenants" />
                          </th>
                          <th style={{ cursor: "pointer" }} onClick={() => toggleSort("created")}>
                            Added <SortIcon field="created" />
                          </th>
                          {view === "shared" && <th>Access</th>}
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((p) => (
                          <tr key={p.id}>
                            <td>
                              <Link to={`/properties/${p.id}`} className="fw-semibold">
                                {p.address || "No address"}
                              </Link>
                            </td>
                            <td>
                              <Badge color="info" className="fw-normal">
                                <i className="bx bx-user me-1" />
                                {tenantCounts[p.id] || 0}
                              </Badge>
                            </td>
                            <td className="text-muted small">
                              {p.createdAt ? p.createdAt.toLocaleDateString() : "—"}
                            </td>
                            {view === "shared" && (
                              <td>
                                <Badge color={canEditProperty(p, user) ? "primary" : "secondary"} className="fw-normal">
                                  {canEditProperty(p, user) ? "Editor" : "Viewer"}
                                </Badge>
                              </td>
                            )}
                            <td>
                              <Link to={`/properties/${p.id}`} className="btn btn-sm btn-outline-primary me-2">
                                <i className="bx bx-show me-1" /> View
                              </Link>
                              {showDeleted ? (
                                <Button
                                  size="sm"
                                  color="success"
                                  outline
                                  disabled={restoring === p.id}
                                  onClick={() => handleRestore(p.id)}
                                >
                                  {restoring === p.id ? <Spinner size="sm" /> : <><i className="bx bx-undo me-1" />Restore</>}
                                </Button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          );
        })()}

        {/* Create Property Modal */}
        <Modal isOpen={createModal} toggle={() => { setCreateModal(false); setSearchParams({}); setAddressVerified(false); setPlaceSearch(""); }}>
          <ModalHeader toggle={() => { setCreateModal(false); setSearchParams({}); setAddressVerified(false); setPlaceSearch(""); }}>
            Add Property
          </ModalHeader>
          <ModalBody>
            {error && <Alert color="danger">{error}</Alert>}

            <FormGroup>
              <Label>
                Search Address *{" "}
                {addressVerified
                  ? <Badge color="success" className="ms-1"><i className="bx bx-check me-1" />Verified</Badge>
                  : <Badge color="warning" className="ms-1 text-dark"><i className="bx bx-search me-1" />Search required</Badge>
                }
              </Label>
              <input
                ref={placeInputRef}
                type="text"
                className="form-control"
                placeholder="Start typing an address…"
                onChange={() => setAddressVerified(false)}
              />
              {!addressVerified && (
                <small className="text-muted">Select an address from the dropdown to verify it.</small>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Street Address *</Label>
              <Input
                type="text"
                value={street}
                onChange={(e) => { setStreet(e.target.value); setAddressVerified(false); }}
                placeholder="123 Main Street"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>City *</Label>
              <Input
                type="text"
                value={city}
                onChange={(e) => { setCity(e.target.value); setAddressVerified(false); }}
                placeholder="New York"
              />
            </FormGroup>
            
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>State *</Label>
                  <Input
                    type="text"
                    value={state}
                    onChange={(e) => { setState(e.target.value); setAddressVerified(false); }}
                    placeholder="NY"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>ZIP Code *</Label>
                  <Input
                    type="text"
                    value={zip}
                    onChange={(e) => { setZip(e.target.value); setAddressVerified(false); }}
                    placeholder="10001"
                  />
                </FormGroup>
              </Col>
            </Row>
            
            <FormGroup>
              <Label>Country</Label>
              <Input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="USA"
              />
            </FormGroup>

            <div className="mt-3 p-2 bg-light rounded" onClick={() => setAgreeToTerms(v => !v)} style={{ cursor: "pointer" }}>
            <FormGroup check className="mb-0">
              <Input
                type="checkbox"
                id="agreeCheckbox"
                defaultChecked={false}
                readOnly
                checked={agreeToTerms}
              />
              <Label check for="agreeCheckbox" className="mb-0" style={{ cursor: "pointer" }}>
                I agree to the <Link to="/terms-of-service" target="_blank">Terms of Service</Link> and{" "}
                <Link to="/privacy-policy" target="_blank">Privacy Policy</Link> *
              </Label>
            </FormGroup>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => { setCreateModal(false); setSearchParams({}); setAddressVerified(false); setPlaceSearch(""); }}>
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={!addressVerified || !street.trim() || !city.trim() || !state.trim() || !zip.trim() || !agreeToTerms || saving}
              onClick={handleCreate}
            >
              {saving ? <Spinner size="sm" /> : "Add Property"}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default PropertyList;
